import React, { useMemo, useState } from 'react';
import { addDays, addWeeks, isSameDay, startOfWeek } from 'date-fns';
import { ChevronDown, ChevronRight, X } from 'lucide-react';

import PageCard from '../../PageCard/PageCard';
import { Segmented } from '../../ui/segmented';
import { cn } from '../../../lib/utils';
import { WORK_DAY_HOURS } from '../../../model/team.model';
import ActivityHeatmap from './components/ActivityHeatmap';
import {
  categories,
  categoryById,
  categoryGroups,
  formatHours,
  isWeekendIndex,
  memberCategoryTotals,
  memberDays,
  activeMembers as members,
} from './teamMock';

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const shortDate = (date: Date) => `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}`;

const PERIOD_OPTIONS = [
  { value: '14', label: 'Last 2 weeks' },
  { value: '7', label: 'This week' },
];

const BREAKDOWN_OPTIONS = [
  { value: 'category', label: 'By category' },
  { value: 'person', label: 'By person' },
];

const sum = (values: number[]) => values.reduce((acc, value) => acc + value, 0);

const TeamPage: React.FC = () => {
  const [period, setPeriod] = useState('14');
  const [breakdown, setBreakdown] = useState('category');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const [expandedPeople, setExpandedPeople] = useState<string[]>([]);

  // Window is the previous week plus the current one; "This week" is its second half.
  const offset = period === '7' ? 7 : 0;

  const days = useMemo(() => {
    const start = addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), -1);
    return Array.from({ length: 14 }, (_, index) => addDays(start, index)).slice(offset);
  }, [offset]);

  const todayIndex = days.findIndex(day => isSameDay(day, new Date()));

  const hoursByMember = useMemo(() => {
    const result: Record<string, number[]> = {};
    memberDays.forEach(entry => {
      result[entry.memberId] = entry.hours.slice(offset);
    });
    return result;
  }, [offset]);

  const isWeekend = (index: number) => isWeekendIndex(index + offset);

  const coverage = useMemo(() => {
    const workdays = days.filter((_, index) => !isWeekend(index)).length;
    const expected = workdays * WORK_DAY_HOURS * members.length;
    const logged = sum(members.map(member => sum(hoursByMember[member.id] ?? [])));

    let emptyWorkdays = 0;
    members.forEach(member => {
      (hoursByMember[member.id] ?? []).forEach((value, index) => {
        if (value === 0 && !isWeekend(index)) emptyWorkdays += 1;
      });
    });

    return {
      logged,
      expected,
      share: expected > 0 ? logged / expected : 0,
      logging: members.filter(member => sum(hoursByMember[member.id] ?? []) > 0).length,
      emptyWorkdays,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, hoursByMember, offset]);

  /**
   * The category split is stored for the full window, so scale it by how much of a
   * person's time falls inside the selected period. Totals stay consistent with the
   * heatmap that way.
   */
  const scaledTotals = useMemo(() => {
    const ratioByMember: Record<string, number> = {};
    memberDays.forEach(entry => {
      const full = sum(entry.hours);
      const windowed = sum(entry.hours.slice(offset));
      ratioByMember[entry.memberId] = full > 0 ? windowed / full : 0;
    });

    return memberCategoryTotals
      .filter(total => !selectedMemberId || total.memberId === selectedMemberId)
      .map(total => ({ ...total, hours: total.hours * (ratioByMember[total.memberId] ?? 0) }));
  }, [offset, selectedMemberId]);

  const visibleTotal = sum(scaledTotals.map(total => total.hours));

  const byGroup = useMemo(
    () =>
      categoryGroups
        .map(group => {
          const groupCategories = categories
            .filter(category => category.groupId === group.id && !category.archived)
            .map(category => ({
              category,
              hours: sum(
                scaledTotals.filter(total => total.categoryId === category.id).map(total => total.hours),
              ),
            }))
            .filter(item => item.hours > 0)
            .sort((a, b) => b.hours - a.hours);

          return { group, categories: groupCategories, hours: sum(groupCategories.map(item => item.hours)) };
        })
        .filter(item => item.hours > 0)
        .sort((a, b) => b.hours - a.hours),
    [scaledTotals],
  );

  const byPerson = useMemo(
    () =>
      members
        .map(member => {
          const items = memberCategoryTotals
            .filter(total => total.memberId === member.id)
            .map(total => ({
              category: categoryById(total.categoryId)!,
              hours: total.hours * (sum(hoursByMember[member.id] ?? []) / (sum(memberDays.find(d => d.memberId === member.id)?.hours ?? [1]) || 1)),
            }))
            .filter(item => item.hours > 0)
            .sort((a, b) => b.hours - a.hours);

          return { member, items, hours: sum(items.map(item => item.hours)) };
        })
        .sort((a, b) => b.hours - a.hours),
    [hoursByMember],
  );

  const maxPersonHours = Math.max(...byPerson.map(entry => entry.hours), 0);

  const selectedMember = members.find(member => member.id === selectedMemberId);

  const toggleGroup = (group: string) =>
    setCollapsedGroups(prev =>
      prev.includes(group) ? prev.filter(item => item !== group) : [...prev, group],
    );

  const togglePerson = (id: string) =>
    setExpandedPeople(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));

  const title = (
    <div>
      <div className="text-base font-medium leading-tight">Team</div>
      <div className="text-xs text-muted-foreground">
        {shortDate(days[0])} – {shortDate(days[days.length - 1])} · {members.length} people
      </div>
    </div>
  );

  return (
    <PageCard
      title={title}
      extra={<Segmented options={PERIOD_OPTIONS} value={period} onChange={value => setPeriod(String(value))} />}
    >
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="People logging"
          value={`${coverage.logging} of ${members.length}`}
          tone={coverage.logging < members.length ? 'warn' : 'plain'}
        />
        <Stat
          label="Coverage"
          value={`${Math.round(coverage.share * 100)}%`}
          hint={`of ${formatHours(coverage.expected)} h expected`}
          tone={coverage.share < 0.8 ? 'warn' : 'good'}
        />
        <Stat
          label="Empty workdays"
          value={coverage.emptyWorkdays}
          tone={coverage.emptyWorkdays > 0 ? 'warn' : 'plain'}
        />
        <Stat label="Logged" value={`${formatHours(coverage.logged)} h`} tone="plain" />
      </div>

      <section className="mb-6">
        <h3 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Activity</h3>
        <ActivityHeatmap
          members={members}
          days={days}
          hoursByMember={hoursByMember}
          isWeekend={isWeekend}
          todayIndex={todayIndex}
          selectedMemberId={selectedMemberId}
          onSelectMember={setSelectedMemberId}
        />
        <div className="mt-2 flex items-center gap-4 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm border border-dashed border-amber-500/50" />
            nothing logged
          </span>
          <span className="flex items-center gap-1">
            less
            {[0.22, 0.42, 0.62, 0.82, 1].map(opacity => (
              <span key={opacity} className="h-3 w-3 rounded-sm bg-foreground" style={{ opacity }} />
            ))}
            more
          </span>
        </div>
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Where time goes</h3>
          <div className="flex items-center gap-2">
            {selectedMember && (
              <button
                type="button"
                onClick={() => setSelectedMemberId(null)}
                className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs transition-colors hover:bg-muted"
              >
                {selectedMember.name}
                <X className="h-3 w-3" />
              </button>
            )}
            <Segmented
              options={BREAKDOWN_OPTIONS}
              value={breakdown}
              onChange={value => setBreakdown(String(value))}
            />
          </div>
        </div>

        {breakdown === 'category' && (
          <div className="space-y-1">
            {byGroup.map(({ group, categories: groupCategories, hours }) => {
              const isCollapsed = collapsedGroups.includes(group.id);
              return (
                <div key={group.id}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className="flex w-full items-center gap-2 rounded px-1.5 py-1.5 text-left transition-colors hover:bg-muted"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="text-sm font-medium">{group.name}</span>
                    <Bar value={hours} total={visibleTotal} />
                    <span className="w-16 shrink-0 text-right text-sm tabular-nums">{formatHours(hours)} h</span>
                    <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                      {Math.round((hours / visibleTotal) * 100)}%
                    </span>
                  </button>

                  {!isCollapsed &&
                    groupCategories.map(({ category, hours: categoryHours }) => (
                      <div key={category.id} className="flex items-center gap-2 py-1 pl-[30px] pr-1.5">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-sm"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="text-sm text-foreground/90">{category.name}</span>
                        <Bar value={categoryHours} total={visibleTotal} color={category.color} />
                        <span className="w-16 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                          {formatHours(categoryHours)} h
                        </span>
                        <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                          {Math.round((categoryHours / visibleTotal) * 100)}%
                        </span>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        )}

        {breakdown === 'person' && (
          <div className="space-y-1">
            {byPerson.map(({ member, items, hours }) => {
              const isExpanded = expandedPeople.includes(member.id);
              return (
                <div key={member.id}>
                  <button
                    type="button"
                    onClick={() => togglePerson(member.id)}
                    disabled={hours === 0}
                    className="flex w-full items-center gap-2 rounded px-1.5 py-1.5 text-left transition-colors hover:bg-muted disabled:opacity-60 disabled:hover:bg-transparent"
                  >
                    {hours > 0 ? (
                      isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      )
                    ) : (
                      <span className="w-3.5 shrink-0" />
                    )}
                    <span className="w-40 shrink-0 truncate text-sm font-medium">{member.name}</span>
                    <StackedBar items={items} total={hours} max={maxPersonHours} />
                    <span className="w-16 shrink-0 text-right text-sm tabular-nums">
                      {hours > 0 ? `${formatHours(hours)} h` : '·'}
                    </span>
                  </button>

                  {isExpanded &&
                    items.map(item => (
                      <div
                        key={item.category.id}
                        className="flex items-center gap-2 py-1 pl-[30px] pr-1.5"
                      >
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-sm"
                          style={{ backgroundColor: item.category.color }}
                        />
                        <span className="text-sm text-foreground/90">{item.category.name}</span>
                        <Bar value={item.hours} total={hours} color={item.category.color} />
                        <span className="w-16 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                          {formatHours(item.hours)} h
                        </span>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <p className="mt-6 text-xs text-muted-foreground">
        Only the categories your organization publishes appear here. Anything people track privately
        in the app stays invisible to you, and they can see exactly where that line sits.
      </p>
    </PageCard>
  );
};

const Bar: React.FC<{ value: number; total: number; color?: string }> = ({ value, total, color }) => (
  <div className="ml-auto h-1.5 w-full max-w-[280px] shrink overflow-hidden rounded-full bg-muted">
    <div
      className={cn('h-full transition-all', !color && 'bg-foreground')}
      style={{ width: `${total > 0 ? (value / total) * 100 : 0}%`, backgroundColor: color }}
    />
  </div>
);

/**
 * Length carries magnitude (share of the busiest person), segments carry composition.
 * Without the outer scaling every row would be the same length and 44 h would look
 * like 78 h.
 */
const StackedBar: React.FC<{
  items: { category: { id: string; color: string; name: string }; hours: number }[];
  total: number;
  max: number;
}> = ({ items, total, max }) => (
  <div className="ml-auto h-1.5 w-full max-w-[320px] shrink overflow-hidden rounded-full bg-muted">
    <div className="flex h-full" style={{ width: `${max > 0 ? (total / max) * 100 : 0}%` }}>
      {items.map(item => (
        <div
          key={item.category.id}
          title={`${item.category.name}: ${formatHours(item.hours)} h`}
          style={{
            width: `${total > 0 ? (item.hours / total) * 100 : 0}%`,
            backgroundColor: item.category.color,
          }}
        />
      ))}
    </div>
  </div>
);

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone: 'plain' | 'good' | 'warn';
}> = ({ label, value, hint, tone }) => (
  <div className="rounded-md border border-border px-3 py-2">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="flex items-baseline gap-1.5">
      <span
        className={cn(
          'text-xl font-semibold tabular-nums',
          tone === 'good' && 'text-emerald-600 dark:text-emerald-500',
          tone === 'warn' && 'text-amber-600 dark:text-amber-500',
        )}
      >
        {value}
      </span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  </div>
);

export default TeamPage;
